import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';

interface DraftPitch {
  id: number;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
}

interface DraftPitchReminderModalProps {
  isOpen: boolean;
  userName: string;
  draftPitches: DraftPitch[];
  onViewDrafts: () => void;
  onRemindLater: () => void;
  onDismiss: () => void;
}

const DraftPitchReminderModal: React.FC<DraftPitchReminderModalProps> = ({
  isOpen,
  userName,
  draftPitches,
  onViewDrafts,
  onRemindLater,
  onDismiss,
}) => {
  if (!isOpen) return null;

  const pitchCount = draftPitches.length;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70">
        <div className="w-full max-w-md bg-white shadow-2xl rounded-xl sm:max-w-lg">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-neutral-200 sm:p-6">
          <div className="flex items-center justify-center w-10 h-10 bg-warning-100 rounded-full">
            <ExclamationTriangleIcon className="w-6 h-6 text-warning-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-black sm:text-xl">
              Draft Pitch Reminder
            </h2>
            <p className="text-sm text-neutral-600">Action required soon</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="mb-6">
            <p className="mb-4 text-sm leading-relaxed text-neutral-700 sm:text-base">
              Hi <span className="font-semibold">{userName}</span>,
            </p>
            <p className="mb-4 text-sm leading-relaxed text-neutral-700 sm:text-base">
              You have <span className="font-semibold text-warning-600">{pitchCount}</span> pitch{pitchCount !== 1 ? 'es' : ''} due in less than two weeks.
              In order to give editors enough time to consider your release, please submit the pitch as soon as possible.
              Editors like to have at least a 2 week window to review pitches.
            </p>
          </div>

          {/* Draft Pitches List */}
          {draftPitches.length > 0 && (
            <div className="mb-6">
              <h3 className="mb-3 text-sm font-semibold text-black">
                You are missing pitches for the following releases:
              </h3>
              <div
                className="overflow-y-auto border border-neutral-200 rounded-lg bg-neutral-50 max-h-64 scrollbar-thin"
              >
                <ul className="divide-y divide-neutral-200">
                  {draftPitches.map((pitch) => (
                    <li key={pitch.id} className="p-3 transition-colors hover:bg-neutral-100">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-black truncate">
                            {pitch.artistName}
                          </p>
                          {pitch.releaseTitle && (
                            <p className="text-xs text-neutral-600 truncate">
                              {pitch.releaseTitle}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-warning-700 bg-warning-100 rounded">
                            {new Date(pitch.releaseDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={onViewDrafts}
              className="w-full sm:text-base"
            >
              View Draft(s)
            </Button>

            <Button
              variant="neutral"
              onClick={onRemindLater}
              className="w-full sm:text-base"
            >
              Remind Me Later
            </Button>

            <Button
              variant="ghost"
              onClick={onDismiss}
              className="w-full bg-transparent text-neutral-500 sm:text-base"
            >
              Dismiss
            </Button>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default DraftPitchReminderModal;
